<template lang="pug">
PagePeriod(
  :bannerImage="require(`./Postmodernisme_01.jpg`)"
  :circleImage="require(`./sirkel_postmodernisme.png`)"
  )
  // Content here
  h3 Tida og samfunnsforholdene

  figure
    img(src="./Postmodernisme_Tida_og_samfunnsforhold1.jpg" alt="Mann i dress fyller bilen med bensin fra ei kanne. Foto.")
    figcaption I 1970 kunne industriminister Sverre Walter Rostoft helle den første norskproduserte bensinen på bilen sin. Foto: NTB / Scanpix

  h5 Velferdsstat og oljeøkonomi
  p I 1960-årene hadde de fleste landene i Europa reist seg etter andre verdenskrig. Parallelt med en stadig økende velstand ble det skapt et velferdssamfunn. Loven om folketrygd kom i 1967 og sørget for at alle borgere fikk rett til alderspensjon og økonomisk hjelp ved for eksempel sykdom, arbeidsledighet, svangerskap og fødsel. 

  p I 1969 ble det funnet olje i Nordsjøen. Inntektene fra oljeindustrien er den viktigste årsaken til at Norge stort sett har hatt en stabilt god økonomi de siste førti årene. 

  h5 Ungdomskultur og hippiebevegelse 
  p Siden 1950-årene hadde ungdomskulturen vært preget av opprør og motstand mot et materialistisk og konformt voksenliv. Mot slutten av 60-årene utviklet ungdomsopprøret seg til å bli et internasjonalt politisk opprør og en generell kamp for frigjøring og antiautoritære verdier. 

  p I mai 1968 kom det til kraftige opptøyer mellom studenter og politi i Paris. Opprøret utviklet seg fort til å bli en politisk massemønstring, som har gitt navn til en generasjon radikale studenter i hele den vestlige verden – de såkalte sekstiåtterne. 

  p I USA vokste 
    em hippiebevegelsen 
    | fram i 1960-årene. 
    em Hippie 
    | kommer av det engelske ordet ”hip”, et slanguttrykk for ”moderne og frigjort menneske”. Rockemusikk, alternativ livsstil og rusmidler var sentrale elementer i hippienes protest mot materialistiske verdier og mot USAs krigføring i Vietnam. 

  figure
    img(src="./Postmodernisme_Tida_og_samfunnsforhold2.jpg" alt="Fem personer med mye hår, store frakker og slengbukser poserer foran et utbrent hus. Foto.")
    figcaption Rockebandet Steppenwolf, kjent for låten «Born to Be Wild». Foto: Henry Diltz / Corbis / Scanpix


  figure.col.col5.right-image
    img(src="./Postmodernisme_Demonstrasjon-mot-EEC.jpg" alt="Fredelig demonstrasjonstog mot EEC på Karl Johan med Slottet i bakgrunnen.Foto.")
    figcaption Demonstrasjon mot EEC i 1972. Foto: Jan A. Martinsen / Aftenposten / Scanpix


  h5 EU-kamp i Norge 
  p Både i 1972 og i 1994 ble det holdt folkeavstemning om EU-medlemskap i Norge, og begge gangene stemte et knapt flertall mot medlemskap i unionen. Gjennom den såkalte EØS-avtalen er norsk økonomi og handel i dag likevel sterkt påvirket av EU-landenes lovverk og politikk.

  h5 Kald krig og et delt Europa
  p Forholdet mellom Sovjetunionen og USA utviklet seg etter andre verdenskrig til å bli svært spent. Denne ”kalde krigen” påvirket hele verdenssamfunnet. Europa var delt i to – de vesteuropeiske landene var stort sett USAs allierte, mens de østeuropeiske landene var i allianse med Sovjetunionen. 

  figure.col.col7.right-image
    img(src="./Postmodernisme_Tida_og_samfunnsforhold4.jpg" alt="To personer som titter over en mur med piggtråd. Foto.")
    figcaption Et par i Vest-Berlin snakker med slektninger i Øst-Berlin. Foto: Bettmann/Corbis/Scanpix

  p Grensene mellom østblokk- og vestblokkland var så godt som stengt, særlig for østeuropeiske borgere, og levestandarden i Øst-Europa var langt lavere enn i de vesteuropeiske landene. 

  p Først mot slutten av 1980-årene forandret situasjonen seg. I 1985 ble Mikhail Gorbatsjov (1931– ) president i Sovjetunionen. Han innførte såkalt perestrojka, økonomiske reformer, og glasnost, åpenhet, og revolusjonerte styreformen i landet på bare få år. 

  h5 Østblokklandene frigjør seg 
  p Høsten 1989 skjedde det en revolusjon i Øst-Europa som førte til at kommunistregimene i de ulike landene kollapset etter tur. I november 1989 falt også muren mellom Øst- og Vest-Berlin, som hadde delt byen i to helt siden 1961. I 1990 ble Vest- og Øst-Tyskland samlet igjen, og i 1991 gikk Sovjetunionen i oppløsning. 

  h5 Bosnia-krigen 
  p I de fleste østblokklandene gikk endringsprosessene i 1990-årene fredelig for seg, men i det tidligere Jugoslavia økte spenningene mellom de ulike folkegruppene. Den verste konflikten oppstod i Bosnia-Hercegovina, der over hundre tusen mennesker ble drept. Krigen gikk hardest ut over den muslimske delen av befolkningen. 

  figure.col.col4.left-image
    img(src="./Postmodernisme_Fane-til-kvinnedagen.jpg" alt="To kvinner holder opp et banner der det står «lønna arbeid til alle kvinner. Husarbeid til alle menn». Foto.")
    figcaption Fane til Kvinnedagen i 1982. Foto: Aftenposten/Scanpix

  h5 Likestillingskamp 
  p 1950-årene var preget av konservative familieverdier: Far jobbet og deltok i samfunnslivet, mor passet hjem og barn. Utover i 60- og 70-årene vokste det fram en sterk kvinnebevegelse både i USA og i Vest-Europa, med krav om likestilling mellom kjønnene. Mange rettigheter som kvinnene kjempet for den gang, er i dag en selvfølge for kvinner i vestlige land.

  h5 Miljøkamp
  p I 1970-årene ble stadig flere opptatt av miljøspørsmål også. Mange var kritiske til et høyt forbruk og bekymret for konsekvensene det kunne få for miljø og mennesker. Særlig skapte byggingen av atomkraftverk sterk motstand, og i flere sentraleuropeiske land ble det stiftet ”grønne partier”.

  p I 1980-årene kom avskoging og utslipp av klimagasser på den miljøpolitiske dagsordenen. Utfordringene er ikke blitt mindre på 2000-tallet. Klimaendringene vi ser i dag, er mest sannsynlig menneskeskapte, men arbeidet med å redusere CO2-utslipp går likevel langsomt. 

  figure.col.col6.right-image
    img(src="./Postmodernisme_Tida_og_samfunnsforhold6.jpg" alt="To skyskrapere med røyk og eksplosjon. Foto.")
    figcaption Angrepet på World Trade Center 11. september 2001. Foto: Carmen Taylor / AP / Scanpix

  h5 Kampen mot terror 
  p 11. september 2001 står som et veiskille i moderne internasjonal politikk og samfunnsliv. Nesten tre tusen mennesker ble drept i terrorangrepet som rammet World Trade Center i New York og Pentagon, det amerikanske militære hovedkvarteret. Ansvarlig for aksjonen var Al-Qaida, en ekstremistisk islamistgruppe ledet av Osama bin Laden. 

  p USA reagerte på terrorangrepet med å lansere krigen mot terrorisme, en global kampanje for å bekjempe internasjonale terroristgrupper og terrornettverk. Men også i perioden etter 2001 har islamistiske grupper gjennomført en rekke terroranslag, ikke bare i vestlige land, men også i Afrika, Midtøsten og Asia. 

  figure
    img(src="./Postmodernisme_Tida_og_samfunnsforhold7.jpg" alt="Syv glade ungdommer med ulik hudfarge poserer på rekke. Foto.")
    figcaption Gode venner på tvers av livssyn, verdier og levesett. Foto: Kerstin Mertens / Samfoto / Scanpix

  h5 Et mer mangfoldig Norge 
  p I 1970 var det 50 000 innvandrere og norskfødte med innvandrerforeldre i Norge, i dag er det 552 000. Vi er blitt kjent med ulike livssyn, verdier og levesett som både utfordrer og beriker norsk kultur og samfunnsliv. 

  h3 Tanker i tida 

  figure
    img(src="./Postmodernisme_Tanker_i_tiden1.jpg" alt="Høyhus i bybilde. Foto.")
    figcaption Aker Brygge i Oslo kan stå som symbol på postmoderne arkitektur i Norge. Foto: Vegard Grøtt / NTB Scanpix
  

  h5 Postmodernitet 
  p Begrepet 
    em postmodernitet 
    | har siden 1980-årene vært brukt som en fellesbetegnelse på den økonomiske og kulturelle tilstanden i de vestlige land i vår samtid. Ordet kan oversettes med ”tiden eller tenkningen etter moderniteten”. For å forstå begrepet må vi derfor vite hva som menes med modernitet. 

  p 
    em Modernitet 
    | kaller vi epoken fra opplysningstiden og fram til vår tid. De sentrale ideene i denne epoken har vært ”de tre f-ene”: troen på frihet, framskritt og fornuft. Vitenskapelig og teknologisk utvikling og politisk og personlig frihet skulle sørge for at menneskene fikk et stadig bedre og lykkeligere liv. Med fornuften som rettesnor skulle det være mulig å komme fram til absolutte sannheter i både vitenskap, politikk og samfunnsliv.

  figure.col.col4.left-image
    img(src="./Postmodernisme_Tanker_i_tiden2.jpg" alt="Portrett av mann i hvit skjorte. Foto.")
    figcaption Jean-François Lyotard. Foto: Bracha L. Ettinger / Wikimedia Commons

  h5 Kritikk av moderniteten
  p Postmoderne tenkere er kritiske til disse ideene. I boka 
    em Den postmoderne tilstand: En rapport om kunnskap 
    | (1979) skriver den franske filosofen 
    strong Jean-François Lyotard 
    | (1924–1998) at ”de store fortellingene er døde”: De overordnete ideene som har preget utviklingen i Vesten de siste tre–fire hundre år, er ikke lenger uomstridte sannheter. Vitenskapen har bidratt til utviklingen av atomvåpen og kjemiske våpen, og den teknologiske utviklingen og industrialiseringen har riktignok gitt mange mennesker bedre levekår, men den har også skapt store globale miljø- og fattigdomsproblemer. 

  h5 Det postsekulære samfunnet 
  p Opplysningstidens tro på frihet og fornuft førte til en kritisk gransking av alle slags autoriteter, også av kirken. Gjennom de siste 200 årene har det skjedd en sekulariseringsprosess der både enkeltmennesket, kulturen og samfunnet i stadig større grad har løsrevet seg fra religiøse autoriteter og normer. 

  p Men er Gud død? I 2005 var det 6,7 millioner besøk under gudstjenester i Den norske kirke. I 2010 var det 142 registrerte trossamfunn og 2 livssynssamfunn bare i Oslo og Akershus. Mange mennesker tror, men hva tror vi på? 
  figure.col.col4.left-image
    img(src="./Postmodernisme_Jurgen-Habermas.jpg" alt="Portrett av smilende mann i dress. Foto.")
    figcaption Jürgen Habermas. Foto: Marit Hommedal / Scanpix


  p Den tyske filosofen og sosiologen 
    strong Jürgen Habermas 
    | (1929– ) omtaler våre vestlige samfunn i dag som postsekulære. Han mener at religionen har en sentral plass, både i enkeltmenneskers liv og i internasjonal storpolitikk. Den dominerende konflikten internasjonalt knyttes i dag til radikal islam, og vi ser en stadig økende interesse for ulike religioner og livssyn. Mennesker søker etter mening og sammenheng i tilværelsen, og åpner opp for både Gud, engler og troen på et liv etter døden. 

  h5 Postmodernisme
  p Fra 1960-årene og utover ser vi nye tendenser og strømninger innenfor kunst, arkitektur og litteratur som vi gjerne sammenfatter i begrepet postmodernisme. Postmodernisme er altså en estetisk teori, en lære om hva kunst er og vil, om forholdet mellom kunstverk og publikum og om forholdet mellom kunstens verden og virkeligheten. 
  
  figure.col.col4.right-image
    img(src="./Postmodernisme_Campbells-Soup.jpg" alt="Et par ser på vegg med mange like plakater av Campell hermetikkboks. Foto.")
    figcaption Andy Warhol: «Campbell’s Soup». Foto: Nelson Almeida/AFP/Scanpix

  p Postmodernismen kan ses som et oppgjør med kravene til innhold og form i den modernistiske kunsten. Der høymodernistene bevisst brøt med tidligere kunstformer og tok avstand fra massekulturen, gjør postmodernistene det stikk motsatte: De kombinerer nytt og gammelt, høykultur og massekultur, de alluderer til kjente tekster, ironiserer over både egne og andres verk – og resultatet er ofte et lekent og frodig uttrykk.

  h3 Litteraturen 1960–1990 

  .flex.portretter
    figure.m1rem
      img(style="width: 198px" src="./Postmodernisme_Mehren.jpg" alt="Portrett av mann med briller og halvlangt hår. Foto.")
      figcaption Stein Mehren
    figure.m1rem
      img(style="width: 198px" src="./Postmodernisme_Kolbein-Falkeid.jpg" alt="Portrett av snakkende, eldre mann med skjegg i kofte. Foto.")
      figcaption Kolbein Falkeid
    figure.m1rem
      img(style="width: 198px" src="./Postmodernisme_Georg-Johannesen.jpg" alt="Portrett av en mann grått hår og skjegg, tørkle i halsen. Foto. ")
      figcaption Georg Johannesen

  h5 1960-årene: Sentrallyrikk i fokus
  p I 1960 er tungetaledebatten, det vil si debatten om den modernistiske lyrikken, lagt død. Det er lite omstridt å skrive dikt uten taktfast rytme eller enderim. Men det skrives fortsatt tradisjonell lyrikk, selv om den frie formen nå dominerer. 

  p En lyriker som debuterte i 1960, var 
    strong Stein Mehren 
    | (1935–2017). Mehren skriver sentrallyriske dikt om død og kjærlighet, og han skriver modernistisk om forholdet mellom språk og erkjennelse. Mehrens poesi er intellektuell og filosofisk, med røtter tilbake til romantikken, men med et blikk fram mot moderniteten og postmoderniteten – som han ofte er veldig kritisk til. 

  p 
    strong Kolbein Falkeid 
    | (1933–2021) debuterte i 1962. Falkeid er kjent for å skrive en kombinasjon av sentrallyrikk og hverdagspoesi, og han er en folkekjær og mye lest poet. Samarbeidet med gruppa Vamp har også ført til at tekstene hans er formidlet til mange. 

  NrkVideo(id="3284")

  p En lyriker som vanskelig lar seg plassere innenfor en retning, er 
    a(href="https://www.nrk.no/kultur/georg-johannesen-om-kassandra-1.6849754" target="_blank")
      strong Georg Johannesen 
    | (1931–2005). Han debuterte i slutten av 1950-årene, men var med på å prege norsk litteratur og norsk litterær debatt i flere tiår. Som politisk radikaler engasjerte han seg sterkt gjennom både dikt, essay, romaner og sakprosatekster – ofte med et kritisk blikk på Norge og nordmenn. 

  h5 Kjøpt: ja – lest: tja 
  p I 1965 etableres 
    em innkjøpsordningen for nyskrevet norsk skjønnlitteratur
    | . Ordningen er en forutsetning for det mangfoldet vi etter hvert får av diktsamlinger i Norge. Stortinget vedtar at 1000 eksemplarer av de fleste norske skjønnlitterære bøker, prosa, drama, lyrikk og gjendiktninger av lyrikk, skal kjøpes inn av staten. Norsk kulturråd administrerer ordningen, og bøkene deles ut til folkebibliotek rundt om i landet.

  figure.col.col6.right-image
    img(src="./Postmodernisme_Litteraturen4.jpg" alt="Tre menn i samtale rundt stuebordet. Foto.")
    figcaption «Dei tre unge opprørarane frå gruppa kring det litterære tidsskriftet Profil» var teksten til dette bildet da det sto på trykk i 1966. Tor Obrestad, Paal-Helge Haugen og Einar Økland.  Foto: NTB / Scanpix

  h5 Profil-gruppa
  p I 1965 samlet en gruppe venstreorienterte unge studenter, skribenter, forfattere og intellektuelle seg og gav ut litteraturtidsskriftet Profil. Profil-forfatterne eksperimenterte med både prosa og lyrikk, og de lanserte en ny form for modernisme – 
    em Profil-modernismen 
    | eller 
    em nyenkel 
    | diktning. 

  p Profil-lyrikerne hentet inspirasjon fra blant annet østlig haikudiktning og skrev dikt med hverdagslige og konkrete motiver. Språklig lot de seg inspirere av hverdagsspråk, muntlig sjargong, poptekster og reklamespråk. Kjente Profil-lyrikere er 
    strong Jan Erik Vold 
    | (1939– ), 
    strong Eldrid Lunden 
    | (1940– ), 
    strong Einar Økland 
    | (1940– ) og 
    strong Paal Helge Haugen (1945– )
    |. 

  .flex.portretter.portretter-4
    figure.m1rem
      img(src="./Postmodernisme_Jan-Erik-Vold.jpg" alt="Portrett av mann med briller og grått hår. Foto.")
      figcaption Jan Erik Vold
    figure.m1rem
      img(src="./Postmodernisme_Eldrid-Lunden.jpg" alt="Smilende kvinne holder blomsterbukett. Foto.")
      figcaption Eldrid Lunden
    figure.m1rem
      img(src="./Postmodernisme_Einar-Okland.jpg" alt="Portrett av mann med briller. Foto.")
      figcaption Einar Økland
    figure.m1rem
      img(src="./Postmodernisme_Paal-Helge-Haugen.jpg" alt="Portrett av mann med grå bart og hår. Foto.")
      figcaption Paal Helge Haugen

  NdlaAudio(
    source="media/periods/postmodernisme/Vold_trikkeskinnediktetdiktet.mp3"
    description="Jan Erik Vold: Trikkeskinnediktet"
    )
    blockquote
      strong «Trikkeskinnediktet»
      br
      br
      | Tenkte jeg skulle skrive et dikt
      br
      | om trikkeskinner.
      br
      | Engang begynte jeg på ett: Byen ligger bundet
      br
      | i sitt nett av trikkeskinner
      br
      | – kom ikke lenger.
      br
      | Gå ut og se på trikkeskinnene, de binder ikke
      br
      | byen fast, de ligger nedfelt i gaten
      br
      | med brostein solidarisk på begge sider, gå ut
      br
      | og se på byen, den er ikke bundet, byer
      br
      | kan ikke bindes. Nei, trikkeskinner
      br
      | er til å legge femøringer på,
      br
      | trikken paraderte
      br
      | på brune mynter opp Theresegate, vi stod i portrom
      br
      | parat til å stikke av om trikken
      br
      | stanset, konduktøren ut! Flatklemte
      br
      | femøringer, store som engelske
      br
      | pennystykker (verdt 8 1/3 øre), gjerne
      br
      | klemt skjeve, ingen elg
      br
      | på femørene dengang men gode gamle hå syv. Trikkeskinner
      br
      | og sykling! en gang gikk jeg på hue
      br
      | foran læreren, det var i svingen ved Sporveisgata
      br
      | utenfor parfymeriet og lekebutikken der, bare med ballongdekk
      br
      | var man trygg (hadde
      br
      | halvballong). Om våren kommer
      br
      | den siste snøen seilende i klumper
      br
      | ned trikkeskinnene, skyver brunt vann foran seg
      br
      | med fine bølgestriper i. Siden blå menn
      br
      | som skyfler skinnene rene så solen
      br
      | skal få klatre uten sand i munnen
      br
      | opp bakken til Adamstua, der leira ligger våt
      br
      | og snart skal bli gras
      br
      | mellom skinnene.       
    
  .mt40

  h5 1970-årene: Sosialrealistisk litteratur 
  p 
    | I 1973 ble et nytt, radikalt parti stiftet: Arbeidernes Kommunistparti (marxist-leninistene) – forkortet AKP (m-l). 
    strong Dag Solstad 
    | (1941– ), 
    strong Espen Haavardsholm 
    | (1945– ) og 
    strong Tor Obrestad 
    | (1938–2020) var blant Profil-forfatterne som ble med i AKP. Nå begynte de å skrive såkalte sosialrealistiske romaner. Litteraturen skulle tjene det arbeidende folket og opprøret mot kapitalismen.

  .flex.portretter
    figure.m1rem
      img(src="./Postmodernisme_Dag-Solstad.jpg" alt="Halvnært portrett av mann med bustete hår og briller. Foto.")
      figcaption Dag Solstad
    figure.m1rem
      img(src="./Postmodernisme_Espen-Haavardsholm.jpg" alt="Portrett av smilende mann med grått skjegg og rutete skjorte. Foto.")
      figcaption Espen Haavardsholm
    figure.m1rem
      img(src="./Postmodernisme_Tor-Obrestad.jpg" alt="Portrett av mann med grått hår. Foto.")
      figcaption Tor Obrestad

  figure.col.col6.right-image
    img(src="./Postmodernisme_Litteraturen12.jpg" alt="Demonstrasjonstog for kvinnefrigjøring. Foto.")
    figcaption Kvinnefronten i 8. marstog i 1973. Foto: Svein Erik Dahl / Samfoto / Scanpix

  h5 Nye kjønnsroller og sosiale endringer
  p Det feministiske engasjementet i 1970-årene førte til radikale endringer i familielivet og til en økt andel kvinner i utdanning og arbeid. Fra 1970-årene får vi derfor en bølge av romaner som tematiserer kjønnsrollene og de sosiale endringene som fulgte av kvinnekampen. 

  p Forfattere som 
    strong Bjørg Vik 
    | (1935–2018), 
    strong Tove Nilsen 
    | (1952– ), 
    strong Anne Karin Elstad 
    | (1938–2012) og 
    strong Herbjørg Wassmo 
    | (1942– ) skriver alle om hvordan kvinner opplever de nye rollene de har fått, og om forventninger knyttet til disse rollene. Også de kvinnelige prosaforfatterne i 1970-årene brukte stort sett et tradisjonelt, realistisk formspråk.

  .flex.portretter.portretter-5
    figure.m1rem
      img(style="width: 120px" src="./Postmodernisme_Bjorg-Vik.jpg" alt="Portrett av kvinne med oppsatt hår. Foto.")
      figcaption Bjørg Vik
    figure.m1rem
      img(style="width: 120px" src="./Postmodernisme_Tove-Nilsen.jpg" alt="Portrett av glad kvinne med blondt hår og hjertesmykke. Foto.")
      figcaption Tove Nilsen
    figure.m1rem
      img(style="width: 120px" src="./Postmodernisme_Anne-Karin-Elstad.jpg" alt="Portrett av kvinne med halvlangt, hvitt hår. Foto.")
      figcaption Anne Karin Elstad
    figure.m1rem
      img(style="width: 120px" src="./Postmodernisme_Herbjorg-Wassmo.jpg" alt="Portrett av glad kvinne med mørkt, halvlangt hår. Foto.")
      figcaption Herbjørg Wassmo
    figure.m1rem
      img(style="width: 120px" src="./Postmodernisme_Kjartan-Flogstad.jpg" alt="Portrett av mann med bart. Han ser ned. Foto.")
      figcaption Kjartan Fløgstad

  h5 Kjartan Fløgstad
  p (1944– ) er en annen sentral forfatter som debuterte i slutten av 1960-årene. Fløgstad har ofte industriarbeidere og sjømenn som hovedpersoner i romanene sine. Han skriver eksperimentelt og lekende, romanene hans er inspirert av modernistiske uttrykksformer og av den latinamerikanske magiske realismen.

  h5 Lyrikken i 1970-årene
  p Også lyrikken i 1970-årene er preget av politikk og samfunnsengasjement, og kamp- og brukslyrikken får derfor en ny oppblomstring. I kampen for et bedre og mer rettferdig samfunn blir det igjen mer akseptert å skrive dikt med fast rytme og enderim. 

  figure
    img(src="./Postmodernisme_Litteraturen18.jpg" alt="Fire musikere som opptrer i stue. Foto.")
    figcaption Visegruppen Ballade øver til en turné i 1978. Fra venstre: Lillebjørn Nilsen, Lars Klevstrand, Åse Kleveland og Birgitte Grimstad. Foto: Paul Owesen / NTB Scanpix

  p Det oppstår også nye arenaer for formidling av dikt. 
    strong Lillebjørn Nilsen 
    | (1950– ), 
    strong Kari Svendsen 
    | (1950– ) og 
    strong Lars Klevstrand 
    | (1949– ) er eksempler på visesangere som framførte egne og andres tekster, gjerne tonesatte dikt – i kampen for fred og likestilling og mot kapitalismen eller krigen i Vietnam. Også arbeiderdiktning og hverdagspoesi ble formidlet gjennom sang. Den populære venstreradikale gruppa Vømmøl gav ut flere LP-er med omfattende tekstvedlegg. 

  .flex.portretter
    figure.m1rem
      img(src="./Postmodernisme_Lillebjorn-Nilsen.jpg" alt="Portrett av mann med langt hår, åpen skjorte med skjerf i halsen. Foto.")
      figcaption Lillebjørn Nilsen
    figure.m1rem
      img(src="./Postmodernisme_Kari-Svendsen.jpg" alt="Sprudlende kvinne opptrer med banjo på scenen. Foto.")
      figcaption Kari Svendsen
    figure.m1rem
      img(src="./Postmodernisme_Lars-Klevstrand.jpg" alt="Portrett av mann i sorte klær utendørs. Foto.")
      figcaption Lars Klevstrand

  NrkVideo(id="74741")
  
  p De norske visesangerne hentet inspirasjon fra USA og andre engelsktalende land. Innflytelsesrike artister som 
    strong Bob Dylan 
    | (1941– ), 
    strong Joni Mitchell 
    | (1943– ) og 
    strong Neil Young (1945– ) 
    | påvirket en hel generasjon gjennom sine sanger. 

  p Den politiske kampen i 1970-årene var også nasjonal i sin karakter. Kampen mot EEC, dagens EU, førte til en interesse for bygda og det særegent norske. Dette viser seg i at folkeliv over hele landet ble brukt som motiv i tekster som gjerne var skrevet på dialekt. 

  h3 Litteraturen i 1980-åra

  h5 Nostalgi og sjarmerende outsidere
  figure.col.col4.right-image
    img(src="./Postmodernisme_Lars-Saabye-Christensen.jpg" alt="Skalla mann med skjegg poserer med hånden bak hode. Foto.")
    figcaption Lars Saabye Christensen

  p       
    strong Lars Saabye Christensen
    | (1953– ) er en av de forfatterne som har solgt mest i nyere tid. Fra 1980-årene har Saabye Christensen lyktes i å skrive kritikerrost litteratur for den brede massen. Romaner som 
    em Beatles 
    | (1984), 
    em Herman 
    | (1988) og 
    em Halvbroren 
    | (2001) har nærmest ikonisk status for generasjonen som var ungdom i 60-årene. Det er ofte en outsiderkarakter vi følger i romanene – en karakter som skildres med varme og humor.     

  .clear 
  .mt20

  h5 Rus, vold og psykiatri 
  figure.col.col4.right-image
    img(src="./Postmodernisme_Ingvar-Ambjornsen.jpg" alt="Portrett av langhåra mann med briller. Foto.")
    figcaption Ingvar Ambjørnsen
  p       
    strong Ingvar Ambjørnsen
    | (1956– ) er en annen forfatter som har skrevet om outsidere, og som tradisjonelt har hatt en sterk posisjon i skolen. Romanen 
    em Hvite niggere 
    | (1986) er fortellingen om noen kamerater som trekkes mer og mer inn i et rusmiljø. Tittelen henspiller på at hovedpersonene ser på seg selv som utstøtte individer i samfunnet. Ambjørnsen er også svært kritisk til tilbudet som psykiatriske pasienter får i Norge, og gjennom bøkene sine om Elling skildrer han varmt og menneskelig livet til en psykiatrisk pasient.

  NrkVideo(id="3244")

  h5 Lyrikken i 1980-årene
  p I løpet av 1980-årene debuterer lyrikere som 
    strong Øyvind Berg 
    | (1959– ), 
    strong Rune Christiansen 
    | (1963– ) og 
    strong Gro Dahle 
    | (1962– ). Samfunnsengasjementet tones ned eller endrer karakter både hos nye og etablerte forfattere, og det blir igjen plass for mer sentrallyriske motiver. 

  .flex.portretter.portretter-4
    figure.m1rem
      img(src="./Postmodernisme_Oyvind-Berg.jpg" alt="Portrett av smilende mann med briller. Foto.")
      figcaption Øyvind Berg
    figure.m1rem
      img(src="./Postmodernisme_Rune-Christiansen.jpg" alt="Portrett av mann med høyhalsa genser. Foto.")
      figcaption Rune Christiansen
    figure.m1rem
      img(src="./Postmodernisme_Gro-Dahle.jpg" alt="Portrett av kvinne med musefletter som holder hendene foran munnen. Foto.")
      figcaption Gro Dahle
    figure.m1rem
      img(src="./Postmodernisme_Triztan-Vindtorn.jpg" alt="Portrett av mann med langt lyst hår. Foto.")
      figcaption Triztán Vindtorn

  p En forfatter som debuterte i 70-årene, men som først slo gjennom i 80-årene, er 
    strong Triztán Vindtorn 
    | (1942–2009). Han er den mest rendyrkete surrealisten i norsk lyrikk, og skrev eksperimentelle dikt der han bryter de fleste språklige konvensjoner.

  h3 Litteraturen 1990–2010

  figure.col.col6.left-image
    img(src="./Postmodernisme_Litteraturen28.jpg" alt="Kvinne i kjole opptrer på scene. Mannlig kollega i bakgrunnen. Foto.")
    figcaption En scene fra 
      em Rambuku 
      | av Jon Fosse på Det Norske Teatret, med Ragnhild Hilt og Svein Erik Brodal. Foto: Carl Martin Nordby / Scanpix

  h5 Språklek og sjangerblanding
  p Litteraturen i tiåret før og etter tusenårsskiftet er omfattende og mangfoldig. Tiden vil vise hva som er mest karakteristisk for vår tid, men én endring er tydelig etter 1985: 
    em Postmodernismen 
    | er kommet til Norge. Forfatterne eksperimenterer med det litterære formspråket, de blander sjangrer, leker med språkformer og stiler og kombinerer ”fint” og folkelig. Dessuten låner de fra andre tekster og andre forfattere, et trekk vi kaller intertekstualitet. 

  p Postmoderne tekster er ofte 
    em metafiksjonale
    | . Med det mener vi at forfatteren bruker grep som visker ut grensene mellom virkelighet og fiksjon. Forfatteren kan for eksempel kommentere skriveprosessen sin i fortellingen, eller han kan selv opptre som figur i den. 

  p I hvilken grad kan språket beskrive virkeligheten? Og hvordan kan det poetiske språket fange og skildre andre sider ved virkeligheten enn den likeframme, realistiske framstillingsmåten? Har det i det hele tatt noen hensikt å prøve? Slike spørsmål opptar ellers veldig ulike forfattere som 
    strong Kjartan Fløgstad
    | , 
    strong Jon Fosse 
    | (1959– ) og 
    strong Cecilie Løveid 
    | (1951– ). 

  p I Norge er det få forfattere som omtaler seg selv som postmoderne, men vi finner eksempler på lyrikk inspirert av postmodernismen hos så ulike forfattere som for eksempel 
    strong Jan Erik Vold 
    | og 
    strong Gro Dahle 
    | . 

  figure.col.col6.left-image
    img(src="./Postmodernisme_Litteraturen29.jpg" alt="Statister i militære kostymer ved kulisser til en sceneoppsetning. Foto. ")
    figcaption Fra innspillingen av filmen«Sofies verden», som bygger på Jostein Gaarders roman. Her er det en scene fra den franske revolusjonen som blir spilt inn. Foto: Rolf Øhman / Scanpix

  h5 Kommersielle suksesser 
  p I 1990- og 2000-årene peker 
    strong Anne B. Ragde 
    | (1957– ) og 
    strong Tore Renberg 
    | (1972– ) seg ut som to tradisjonelle forfattere med stor suksess – Ragde med sin romantrilogi om Torunn som arver en gård etter en far hun aldri har kjent, og Renberg med sin serie om Jarle Klepp, en humoristisk skildring av hvordan det var å være ung i 1990-årene. 

  p I tillegg kommer 
    strong Jostein Gaarders 
    | (1952– ) filosofiske ungdomsroman 
    em Sofies verden 
    | (1991), som er oversatt til 54 språk og har solgt i over 30 millioner eksemplarer. 

  .flex.portretter
    figure.m1rem
      img(src="./Postmodernisme_Anne-B.-Ragde.jpg" alt="Portrett av kvinne med kort, sort hår. Foto.")
      figcaption Anne B. Ragde
    figure.m1rem
      img(src="./Postmodernisme_Tore-Renberg.jpg" alt="Halvnært portrett av mann med kort hår. Foto.")
      figcaption Tore Renberg
    figure.m1rem
      img(src="./Postmodernisme_Jostein-Gaarder.jpg" alt="Portrett av mann med kort skjegg. Foto.")
      figcaption Jostein Gaarder

  ImageTextBlock.mt30(
    :image="require(`./Postmodernisme_Litteraturen33.jpg`)"
    altText="Nærbilete av mann med gitar i bakgrunnen. Foto."    
    )
    h5 Jan Kjærstad 
    p 
      | (1953– ) regnes som en av de mest sentrale postmoderne forfatterne i norsk litteratur. I sine romaner fra 1980- og 1990-årene eksperimenterer han med form, fortelleteknikk, komposisjon og karakterbygging.  

  ImageTextBlock(
    :image="require(`./Postmodernisme_Litteraturen34.jpg`)"
    altText="Portrett av mann med hånden på haka. Foto."    
    )
    h5 Jon Fosse 
    p 
      | (1959– ) er blitt kalt ”den nye Ibsen”. Fra 1990-årene har Fosse hatt stor internasjonal suksess med dramaene sine, og i likhet med Ibsen er hans teaterteknikk nyskapende og utforskende. Fosses forfatterskap er allerede av stort omfang – han har gitt ut en rekke verk og har også skrevet romaner og lyrikk.

  h5 Familien i en postmoderne tid
  p 
    strong Hanne Ørstavik 
    |  (1969– ) og 
    strong Trude Marstein 
    | (1973– ) tematiserer hvor galt det kan gå når mennesker ikke evner å skape gode relasjoner til hverandre. I et samfunn der vi selv må skape mening i tilværelsen, blir de nære relasjonene ekstra viktige. Hjemmet og familien er dessuten en trygg base som vi trenger i en verden der miljøødeleggelser, naturkatastrofer og krig rammer verden over. Når familien svikter, blir konsekvensene derfor katastrofale for karakterene i Marsteins og Ørstaviks bøker. 

  .flex.portretter.portretter-5
    figure.m1rem
      img(src="./Postmodernisme_Hanne-Orstavik.jpg" alt="Portrett av smilende kvinne med kort, hvitt hår. Foto.")
      figcaption Hanne Ørstavik
    figure.m1rem
      img(src="./Postmodernisme_Trude-Marstein.jpg" alt="Portrett av kvinne med hånden på kinnet. Foto.")
      figcaption Trude Marstein
    figure.m1rem
      img(src="./Postmodernisme_Lars-Ramslie.jpg" alt="Halvnært portrett av mann som bøyer hodet litt og ser opp. Foto.")
      figcaption Lars Ramslie
    figure.m1rem
      img(src="./Postmodernisme_Erlend-Loe.jpg" alt="Portrett av skallet mann med rutete skjorte. Foto. ")
      figcaption Erlend Loe
    figure.m1rem
      img(src="./Postmodernisme_Helene-Uri.jpg" alt="Portrett av smilende kvinne med langt mellomblondt hår. Foto.")
      figcaption Helene Uri

  NrkVideo(id="3412")

  figure.col.col4.left-image
    img(src="./Postmodernisme_Bilde-fra-Tatt-av-kvinnen.jpg" alt="Mann med ryggsekk sitter ved siden av ei kvinne under et laken på scenen. Foto.")
    figcaption Gard B. Eidsvold og Helle Haugen i en scene fra Centralteatrets oppsetning av skuespillet «Tatt av kvinnen», som bygger på Erlend Loes roman. Foto: Erlend Aas / Scanpix

  h5 Den postmoderne mannen – ”tatt av kvinnen” 
  p Også i den postmoderne litteraturen er kjønnsroller et gjennomgangstema, og det postmoderne perspektivet er ofte mannens perspektiv. Mennene har mistet evnen til å ta initiativ overfor kvinnen – de vet ikke lenger hva kvinner vil ha. Dette ser vi særlig i 
    em Fatso (2003) av 
    strong Lars Ramslie 
    | (1974– ) og i 
    em Tatt av kvinnen (1993) av 
    strong Erlend Loe 
    | (1969– ). De mannlige karakterene er her tafatte og ensomme og totalt prisgitt en aktiv kvinne. 

  p I 
    em Dyp rød 315 
    | (2001) av 
    strong Helene Uri 
    | (1964– ) møter vi en kvinne som reduserer mannen til et objekt og et tilbehør på lik linje med skjerf og hansker. Den kvinnelige hovedkarakteren Gina Beck soler seg i glansen av en mann som hun trer på armen som en ny veske. Og når denne mannen ikke lenger tilfredsstiller hennes behov, bytter hun ham ut. 

  NrkVideo(id="3246")


  figure.col.col4.right-image
    img(src="./Postmodernisme_Litteraturen41.jpg" alt="Kvinnelig forfatter på bokbad. Foto. ")
    figcaption Åsne Seierstad på Litteraturhuset i 2008. Foto: Knut Falch / Scanpix

  h5 Nye problemstillinger
  p De store konfliktene i litteraturen i dag handler ikke lenger om form, men om grensene mellom fiksjon og sakprosa og om de verdivalg forfatterne gjør. I hvor stor grad kan man basere seg på virkelige personer og hendinger når man skriver skjønnlitteratur? Hva er forskjellen på en roman og en dokumentarbok? 

  figure.col.col4.left-image
    img(src="./Postmodernisme_Litteraturen42.jpg" alt="Mann med kort hår, skjeggstubber og skinnjakke. Foto. ")
    figcaption Karl Ove Knausgård. Foto: Tore Meek / Scanpix

  p Disse spørsmålene er blitt særlig aktuelle etter dommen mot 
    strong Åsne Seierstad 
    | (1970– ) i 2010. Seierstad ble dømt til å betale Shah Mohammad Rais erstatning for å ha basert romanen 
    em Bokhandleren i Kabul 
    | (2002) på livet til Rais og hans familie. Også det enorme seksbindsverket til 
    strong Karl Ove Knausgård 
    | (1968– ), 
    em Min Kamp 
    | (2009–2011), synliggjør de etiske dilemmaene ved å skrive så tett opp til eget liv og erfaring: Hva skjer når ekskoner, nære familiemedlemmer og en avdød far blir romankarakterer?

  NrkVideo.mt20(id="5980")

  h3 Språket i perioden

  figure
    img(src="./Postmodernisme_Spraaket_i_perioden1.jpg" alt="Mann får gave mens flere mennesker ser på. Foto.")
    figcaption Elever fra Oddernes gymnas overleverte i 1975 ca. 25 000 underskrifter til stortingspresident Guttorm Hansen (til høyre) for å be Stortinget om å ta sidemålsstilen opp til revisjon. Men sidemålsundervisningen i skolen står støtt også i dag. Foto: Erik Berglund / Aftenposten / Scanpix

  h5 Språkfred
  p Mens språksituasjonen i Norge i 1950-årene var preget av konflikt og massiv motstand mot samnorskpolitikken, så vi ved inngangen til 1960-årene tegn til mildvær i språkstriden. Da en komité – populært kalt språkfredskomiteen – ble oppnevnt i 1963, hadde den ikke mandat til å lage forslag til en ny rettskrivingsreform. Komiteen skulle komme med forslag til hvordan vi kunne ta vare på og utvikle den norske språkarven. Samnorsktanken var for betent til at de politiske partiene ønsket å gå videre med dette arbeidet. 

  p I tiden etter 1960 stabiliserte språksituasjonen i Norge seg i det todelte landskapet som vi også har i dag. Bokmål er den helt klart dominerende målformen, mens nynorsk har en oppslutning på rundt 15 prosent. Nynorsk står sterkest på Vestlandet, men har også en sterk posisjon innen litteratur og utdanning. I skolen er det fortsatt obligatorisk sidemålsundervisning, og i enkelte humanistiske fag på universitetene står nynorsk også sterkt. 

  p I offentlig forvaltning kreves det at de ansatte mestrer begge målformer. Myndighetene har klare retningslinjer for hvor mye av offentlig kommunikasjon og publikasjoner som skal være på henholdsvis bokmål og nynorsk. I NRK er det krav om 25 prosent nynorsk skrift og normert tale. Bokmål er nærmest enerådende på områder som populærkultur, økonomi og teknologi. 

  h3 Andre kunstformer 

  figure
    img(src="./Postmodernisme_Andre_kunstformer1.jpg" alt="Kunstverk av sovende barn i ramme. Foto. ")
    figcaption Odd Nerdrum: «Baby» (1993). Foto: Bjørn Reese / VG / Scanpix

  h5 Bildekunst
  figure.col.col4.left-image
    img(src="./Postmodernisme_Campbells-Soup.jpg" alt="Et par ser på vegg med mange like plakater av Campell hermetikkboks. Foto.")
    figcaption Andy Warhol: «Campbell’s Soup»

  p Da 
    strong Andy Warhol 
    | (1928–1987) i 1960-årene stilte ut bilder av Campbell tomatsuppe, ble publikum tvunget til å tenke over hva kunst egentlig er. Warhol tok kjente produkter og gjenstander ut av sin vanlige sammenheng og gjorde dem til kunst. På den måten ble han en forløper for postmoderne kunstretninger der ideen bak kunstverket var viktigere enn selve produktet. 

  figure.col.col4.left-image
    img(src="./Postmodernisme_Pushwagner_.jpg" alt="Eldre mann med solbriller og dress foran tre av sine kunstverk. Foto.")
    figcaption Pushwagner (Terje Brofos)

  p Den norske kunstneren 
    strong Terje Brofos 
    | (1940–2018), kjent under navnet 
    strong Hariton Pushwagner 
    |, benytter seg av popkunstens formspråk. Som den amerikanske ”popart”-kunstneren 
    strong Roy Lichtenstein 
    | (1923–1997) bruker Pushwagner tegneserien, en tradisjonell lavkultursjanger, som utgangspunkt. Han er blant annet kjent for bilder som viser den uoversiktlige bytilværelsen og det fremmedgjorte gjennomsnittsmennesket. 

  p I Norge markerer 
    strong Odd Nerdrum 
    | (1944– ) seg som en av de mest sentrale postmoderne kunstnerne. I motsetning til andre samtidige kunstnere har Nerdrum valgt et formspråk lik barokkmalernes på 1600-tallet. Men motivene hans er hentet fra samtiden, og på den måten blander Nerdrum nytt og gammelt. Nerdrum kaller selv bildene sine kitsch, for å markere at han tar avstand fra samtidens etablerte kunstsyn. 

  figure.col.col4.right-image
    img.col.col12(src="./Postmodernisme_Marianne-Heske.jpg" alt="Kvinne sitter i en kunstinstallasjon med mange små glassfigurer. Foto.")
    figcaption Marianne Heske på en utstilling på Henie Onstad Kunstsenter. Foto: Mimsy Møller / Samfoto / NTB

  p En annen sentral norsk kunstner i den postmoderne tradisjonen er 
    strong Marianne Heske 
    | (1946– ). Marianne Heske bestiller produkter av lokale håndverkere, som hun så setter sammen til installasjoner, bilder eller skulpturer. En slik konseptuell kunst – der ideen er viktigere enn selve håndverket – er typisk for den postmoderne retningen.

  figure.col.col4.left-image
    img(src="./Postmodernisme_Kjartan-Slettemark.jpg" alt="Mann utkledd i kunstnerisk kostyme. Foto.")
    figcaption Kjartan Slettemark


  p 
    strong Kjartan Slettemark 
    | (1932–2008) er en annen sentral kunstner i denne perioden. Slettemarks kunst er både sterkt politisk, eksperimentell og lekende i formen. Slettemark er også kjent for å eksperimentere med ulike medier i kunsten og for sin performancekunst. 

  .clear
  h5 Arkitektur 

  figure.col.col5.right-image
    img(src="./Postmodernisme_Andre_kunstformer6.jpg" alt="Fasade på bygg med mye glass. Foto.")
    figcaption Aker Brygge

  p 
    strong Aker Brygge 
    | i Oslo kan stå som symbol på postmoderne arkitektur i Norge. Fram til 1982 var Aker Brygge et travelt industristed der Akers mekaniske Verksted bygde skip. Etter at denne virksomheten ble lagt ned, ble hele industriområdet gjort om til et sted for underholdning, forretninger og boliger for pengesterke mennesker. Stedet kan dermed symbolisere overgangen fra et industrisamfunn til et kommersielt mediesamfunn. Arkitekturen er også typisk for den postmoderne estetikken – arkitektene har nemlig integrert de gamle industrihallene i de moderne bygningene som ble oppført. Denne blandingen av ny og gammel arkitektur er et typisk postmoderne trekk.

  h5 Musikk
  p Fra 1960-årene og fram til vår tid har rock og pop vært de klart dominerende musikksjangrene. Det musikalske uttrykket spenner vidt – fra lett diskomusikk i 1970-årene til opprørsk punk i 1980-årene og glatte ”boyband” i 1990-årene. Felles er evnen til å formidle følelser, enten det er snakk om kjærlighetssorg, politisk urett eller gleden over å være ung. Sammen med filmen har pop- og rockemusikken en enestående identitetsskapende posisjon hos ungdom som annen litteratur og kunst aldri har hatt. 

  figure
    img(src="./Postmodernisme_Stones.jpg" alt="Mannlig vokalist på scenen foran stort publikum på en fotballstadion. Foto.")
    figcaption Mick Jagger i aksjon under en Rolling Stones-konsert.

  p I 1960-årene var musikken knyttet til samtidens ungdomsopprør, og grupper som 
    strong Beatles 
    | og 
    strong Rolling Stones 
    |ble i likhet med 
    strong Bob Dylan 
    | (1941– ) viktige symboler for datidens ungdommer. I 70-årene overtok den litt mer svevende og drømmende hippiekulturen, og artister som 
    strong Janis Joplin 
    | (1943–1970), 
    strong Jimi Hendrix 
    | (1942–1970) og 
    strong Jim Morrison 
    | (1943–1971, med gruppa Doors) ble viktige stemmer her. 


  figure
    img(src="./Postmodernisme_Aha.jpg" alt="Musikkgruppe fremfører låt på scene. Foto.")
    figcaption Foto: Knut Snare / Aftenposten / NTB

  p Bandet 
    strong a-ha 
    | er uten tvil den største internasjonale norske musikksuksessen i perioden etter 1960. A-ha klarte med ”Take on me” noe ingen norske band har klart verken før eller seinere – de solgte til førsteplass på den amerikanske ”billboard”-lista. 

  ImageTextBlock(
    :image="require(`./Postmodernisme_Andre_kunstformer10.jpg`)"
    altText="Mannlig saksofonist på scene. Foto."    
    imageCaption="Jan Garbarek. Foto: Knut Fjeldstad / Scanpix" 
    )
    h5 Jan Garbarek 
    p 
      | (1947– ) er en av få norske musikere med internasjonal anerkjennelse som stilskaper innen jazzen. Garbarek er kjent for å blande inn folketoner i jazzen, og for sitt samarbeid med poeten Jan Erik Vold. Album som 
      em Briskeby Blues 
      | (1969) og 
      em Ingentings bjeller 
      | (1977) nådde et bredt publikum. 

  ImageTextBlock(
    :image="require(`./Postmodernisme_Andre_kunstformer11.jpg`)"
    altText="Kvinnelig vokalist på scene. Foto."    
    imageCaption="Mari Boine. Foto: Mimsy Møller / Samfoto / Scanpix"  
    )
    h5 Mari Boine 
    p 
      | (1956– ) er en annen norsk artist med internasjonal suksess. Musikken hennes er sterkt inspirert av samiske folketoner og joik. Hun bruker jazz og rock til å fornye og modernisere den samiske musikken – en musikk som ofte blir omtalt som ”verdensmusikk”. 
      
  h5 Massemedier
  p Etter at NRK ble offisielt åpnet i 1960, vokste tv-mediet fort. Satellitter gjorde det tidlig mulig å få inn hele verden i stua, og i 1969 kunne vi følge århundrets store begivenhet: De første skrittene på månen ble direkte overført på tv. 

  p Fram til 1982 hadde NRK monopol på å sende fjernsyns- og radioprogrammer, men så ble det åpnet opp for andre aktører. Både lokale kringkastere og internasjonale satellittoverførte kanaler skapte konkurranse. 

  figure
    img(src="./Postmodernisme_Gerd-Liv-Valla.jpg" alt="Kvinne foran mange mikrofoner på en pressekonferanse. Foto.")
    figcaption I dag fins det et mangfold av tv- og radiokanaler som formidler nyheter til norske seere og lyttere. Bildet er tatt i 2007 og viser en pressekonferanse med daværende LO-leder Gerd-Liv Valla. 

  p Et typisk trekk ved moderne mediehistorie er 
    em kommersialisering
    | . Et ledd i kommersialiseringen av mediene er økt tabloidisering og kjendisdyrking. Avisene VG og Dagbladet og ukebladet Se og Hør representerer på ulike måter presse som bruker kjendis- og livsstilsstoff for å nå ut til et større publikum. Men også tv og radio selger kjendisstoff – og de skaper kjendiser. 

  p Dagens medievirkelighet er preget av 
    em globalisering
    | . Store deler av verden er i dag bare et tastetrykk eller en av-og-på-knapp unna. Særlig har Internett satt fart i denne globaliseringstendensen. Både Internett og mobiltelefonen fikk sitt gjennombrudd i midten av 1990-årene, og de har siden skapt store endringer i folks hverdag. 

  p Et annet typisk trekk ved mediene etter 2005 er den økte bruken av sosiale medier, for eksempel blogger, Facebook, Twitter, YouTube og Instagram. Vi ser også tendenser til at disse mediene påvirker tradisjonelle medier, og denne utviklingen vil vi sikkert se mer av i framtiden.

</template>

<script>
import ImageTextBlock from "@/components/ImageTextBlock";
import PagePeriod from "@/components/PagePeriod";
import NdlaVideo from "@/components/NdlaVideo";
import NdlaAudio from "@/components/NdlaAudio";
import NrkVideo from "@/components/NrkVideo";

export default {
  name: "PostmodernismeNB",
  components: {
    ImageTextBlock,
    NdlaAudio,
    NdlaVideo,
    NrkVideo,
    PagePeriod,
  },
};
</script>
